import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';

import { leaseSituationTagMap } from 'common/frontConstants';

import H1 from 'client/components/H1';
import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import ButtonDeprecated, { BUTTON_THEME_BLUE } from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_UPDATE_RENTER_FRONT_TAGS,
} from 'api/actionTypes';
import { SCREEN_ID_APARTMENT_LOCATOR_EDIT_LEAD } from 'client/constants/screenIds';

import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import { leaseSituationShortOptionItems } from 'common/leaseSituationOptionItems';
import bedroomOptionItems from 'client/helpers/bedroomOptionItems';
import branch from 'client/core/branch';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

const formFields = [
  {
    id: 'name',
    title: 'name',
    type: 'short_text',
    validations: ['required'],
  },
  {
    id: 'email',
    title: 'email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'phone',
    title: 'phone',
    type: 'short_text',
    validations: ['required', 'phone'],
  },
  {
    id: 'moveInDate',
    title: 'move-in date',
    type: 'date_picker',
    properties: {
      placeholder: 'Choose date...',
    },
    validations: ['required'],
  },
  {
    id: 'leaseSituation',
    title: 'lease situation',
    type: 'select',
    properties: {
      choices: leaseSituationShortOptionItems,
      multipleSelection: false,
      allowEmptyArray: false,
    },
    validations: [],
  },
  {
    id: 'searchFilters.minRentPrice',
    title: 'minimum rent price',
    type: 'currency',
    validations: ['required'],
  },
  {
    id: 'searchFilters.maxRentPrice',
    title: 'maximum rent price',
    type: 'currency',
    validations: ['required'],
  },
  {
    id: 'searchFilters.bedrooms',
    title: 'bedrooms',
    type: 'multiple_choice',
    properties: {
      choices: bedroomOptionItems,
      multipleSelection: true,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
  {
    id: 'searchFilters.pets',
    title: 'pets',
    type: 'multiple_choice',
    properties: {
      choices: [
        {
          label: 'Dog',
          value: 'dog',
        },
        {
          label: 'Cat',
          value: 'cat',
        },
      ],
      multipleSelection: true,
      allowEmptyArray: true,
    },
  },
];

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  updateLeadApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
  },
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_EDIT_LEAD)
@branch({
  currentUser: ['currentUser'],
  leadUser: ['apartmentLocator', 'lead', 'user'],
})
class AgentLeadEditPage extends Component {
  static fetchData(match, inject) {
    return ApartmentLocatorActions.getLead(inject, parseInt(match.params.renterUserId, 10));
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    leadUser: PropTypes.object,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
  };

  static getPageMetadata = () => ({
    pageTitle: 'Edit Lead | Sunroom Rentals',
    section: 'Edit Lead',
  });

  _onUpdateLead = (values) => {
    const { dispatch, leadUser, history } = this.props;
    const leadUpdates = { ...values };

    Object.keys(values).forEach((key) => {
      if (!leadUser[key] && !values[key]) {
        delete leadUpdates[key];
      }
    });

    Object.keys(leadUser.searchFilters).forEach((key) => {
      if (leadUpdates.searchFilters[key] === undefined) {
        leadUpdates.searchFilters[key] = leadUser.searchFilters[key];
      }
    });

    if (leadUpdates.moveInDate) {
      leadUpdates.searchFilters.moveInDate = leadUpdates.moveInDate;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'users',
        where: {
          id: leadUser.id,
        },
        updates: leadUpdates,
      },
    })
      .then(() => {
        dispatch(ApartmentLocatorActions.getLead, leadUser.id);
        if (leadUpdates.leaseSituation && leadUpdates.leaseSituation !== leadUser.leaseSituation) {
          const allLeaseTags = Object.values(leaseSituationTagMap);
          const frontTagToAdd = leaseSituationTagMap[leadUpdates.leaseSituation];
          const frontTagsToRemove = allLeaseTags.filter((tag) => tag !== frontTagToAdd);

          dispatch(postWithState, {
            action: SR_API_ACTION_UPDATE_RENTER_FRONT_TAGS,
            payload: {
              userId: leadUser.id,
              tagsToAdd: [frontTagToAdd],
              tagsToRemove: frontTagsToRemove,
            },
          });
        }
      })
      .then(() => {
        if (typeof global.alert === 'function') {
          global.alert('User updated!');
        }

        history.push(`/agent/lead/${leadUser.id}`);
      });
  };

  render() {
    const { updateLeadApiState, currentUser, leadUser, match } = this.props;

    return (
      <div className="page-agentDashboard lead edit">
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={
            <SrLink
              scrollToLastPosition
              redirectToLastLocation
              className="back link"
              to={`/agent/lead/${match.params.renterUserId}`}
            />
          }
        />

        <div className="contentContainer">
          {leadUser && (
            <Formik
              enableReinitialize
              initialValues={createInitialValues({
                formFields: formFields,
                defaultValues: leadUser,
              })}
              validateOnChange={false}
              validate={createFormValidationFn(formFields)}
              onSubmit={this._onUpdateLead}
              render={(formikBag) => {
                return (
                  <div>
                    <div className="header">
                      <H1>Edit Lead Info</H1>
                    </div>
                    <Form className="leadForm">
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                        />
                      ))}
                      {updateLeadApiState.error && (
                        <div style={{ color: 'red' }}>
                          {updateLeadApiState.error.detail || updateLeadApiState.error.message}
                        </div>
                      )}
                      <div className="bottomActions">
                        <ButtonDeprecated
                          theme={BUTTON_THEME_BLUE}
                          onClick={formikBag.handleSubmit}
                          disabled={!formikBag.isValid || updateLeadApiState.inProgress}
                        >
                          {updateLeadApiState.inProgress ? 'updating...' : 'update'}
                        </ButtonDeprecated>
                      </div>
                    </Form>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default AgentLeadEditPage;
